'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { getResponseJwtHeader } from '@/utils/web';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import queryKeys from '@/constants/query-keys';
import { ApiQueryBasePromise } from '@/types';
import { getAuthToken } from '@/store/auth';
import { useGetSiteConfigs, useGetSiteConfigsWithSession } from './use-site-configs';

export interface SportsByWebMarketType {
	l?: number;
	t?: number;
	e?: number;
	o?: number;
}

interface SportsByWebResponse {
	days: Record<string, number> | Record<string, never>;
	marketType: SportsByWebMarketType | Record<string, never>;
}

interface EventSummaryDetail {
	[sportId: string]: {
		[matchId: string]: string;
	};
}

interface EventSummaryByWebResponse {
	matchDetail: EventSummaryDetail | Record<string, never>;
}

const useSectionSportsQueryEnabled = (sportTypeList: number[], countryCode: string) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { SiteConfigsWithSessionData } = useGetSiteConfigsWithSession();

	return Boolean(SiteConfigsWithSessionData?.EnablePhPortal) && 
		Boolean(SiteConfigsData?.DirectApiUrl) &&
		sportTypeList.length > 0 && 
		countryCode.length > 0;
};

const fetchSectionSportsContributor = async ({
	directApiUrl, 
	sportTypeList, 
	countryCode, 
	siteId
}:{
	directApiUrl: string;
	sportTypeList: number[];
	countryCode: string;
	siteId: string;
}): Promise<ApiQueryBasePromise<SportsByWebResponse>> => {
	const authToken = getAuthToken();

	if (!directApiUrl) {
		throw new Error('DirectApiUrl not found in site config');
	}

	// 构建查询参数
	const params = new URLSearchParams();
	sportTypeList.forEach(sportId => {
		params.append('sportTypeList', sportId.toString());
	});
	params.append('countryCode', countryCode);
	params.append('siteId', siteId);

	const apiUrl = `${directApiUrl}/CommonService/api/GetSportsByWeb?${params.toString()}`;

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl))(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useSectionSportsContributor = (directApiUrl: string, sportTypeList: number[], countryCode: string) => {
	const enabled = useSectionSportsQueryEnabled(sportTypeList, countryCode);
	const { SiteConfigsData } = useGetSiteConfigs();
	const siteId = SiteConfigsData?.GetMessageID || '';
	return useQuery({
		queryKey: [queryKeys.SPORTS_CONTRIBUTOR],
		queryFn: () => fetchSectionSportsContributor({ directApiUrl, sportTypeList, countryCode, siteId }),
		refetchInterval: 60000,
		cacheTime: 0,
		staleTime: 0,
		refetchOnWindowFocus: false,
		enabled
	});
}; 

const fetchSectionSportsEventSummary = async ({
	directApiUrl, 
	sportTypeList, 
	countryCode, 
	marketType,
	siteId
}:{
	directApiUrl: string;
	sportTypeList: number[];
	countryCode: string;
	marketType: string;
	siteId: string;
}): Promise<ApiQueryBasePromise<EventSummaryByWebResponse>> => {
	const authToken = getAuthToken();

	if (!directApiUrl) {
		throw new Error('DirectApiUrl not found in site config');
	}
	const params = new URLSearchParams();
	sportTypeList.forEach(sportId => {
		params.append('sportTypeList', sportId.toString());
	});
	params.append('countryCode', countryCode);
	params.append('marketType', marketType);
	params.append('siteId', siteId);
	const apiUrl = `${directApiUrl}/CommonService/api/GetEventSummaryByWeb?${params.toString()}`;

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl))(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useSectionSportsEventSummary = (directApiUrl: string, sportTypeList: number[], countryCode: string, marketType: string) => {
	const isQueryEnabled = useSectionSportsQueryEnabled(sportTypeList, countryCode);
	const { SiteConfigsData } = useGetSiteConfigs();
	const siteId = SiteConfigsData?.GetMessageID || '';
	return useQuery({
		queryKey: [queryKeys.SPORTS_EVENT_SUMMARY],
		queryFn: () => fetchSectionSportsEventSummary({ directApiUrl, sportTypeList, countryCode, marketType, siteId }),
		refetchInterval: 60000,
		cacheTime: 0,
		staleTime: 0,
		refetchOnWindowFocus: false,
		enabled: isQueryEnabled && marketType.length > 0
	});
}; 